import { createOfflineAccess } from "../../utils/offlineAccess";
import uuid from "react-native-uuid";
import { captureException } from "@utils/captureException";
import { createPlayOutbox } from "../../utils/player/features/analytics/createPlayOutbox";
import { createPlayOutboxStorage } from "../../utils/player/features/analytics/createPlayOutboxStorage";
import { getPlayDeviceId } from "../../utils/player/features/analytics/playDeviceId";
import { playOutboxLock } from "../../utils/player/features/analytics/playOutboxLock";
import { createTRPCUntypedClient, createTRPCClientProxy } from "@trpc/client";
import { createTRPCQueryUtils } from "@trpc/react-query";
import type { DefaultOptions } from "@tanstack/react-query";
import type { AppRouter } from "api";
import { createDurableCache } from "@utils/durableCache/createDurableCache";
import type { AuthSession } from "../../runtime/authSession";
import type { DevicePorts } from "../../runtime/ports";
import { createHeaders, createLoggerLink } from "./headers";
import { createQueryClient } from "./queryClient";
import { sessionLink } from "./sessionLink";
import { albumsQuery, playlistQuery } from "../queries/collections";
import { AUTH_QUERY_KEY } from "@utils/queryKeys";

export type DataQueryUtils = ReturnType<typeof createTRPCQueryUtils<AppRouter>>;

let nextGeneration = 0;

export function openDataSession(
  ports: Omit<DevicePorts, "auth"> & { auth: AuthSession },
  defaults?: DefaultOptions,
) {
  const generation = ++nextGeneration;
  const controller = new AbortController();
  const isCurrent = () => !controller.signal.aborted;
  const staleWrites: (readonly unknown[])[] = [];
  const canWrite = (queryKey: readonly unknown[]) => {
    if (isCurrent()) return true;
    staleWrites.push(queryKey);
    return false;
  };
  const cache = createDurableCache({ db: ports.db, isCurrent, canWrite });
  const queries = createQueryClient({
    db: ports.db,
    kv: ports.kv,
    cache,
    appVersion: ports.platform.appVersion,
    defaults,
  });
  const headers = (op: Parameters<typeof createHeaders>[0]) =>
    createHeaders(op, {
      auth: ports.auth,
      platform: ports.platform,
      solanaAvailable: () =>
        !!queries.queryClient.getQueryData(["solana", "isAvailable"]),
    });
  const trpcClient = createTRPCUntypedClient<AppRouter>({
    links: [
      sessionLink({ signal: controller.signal, isCurrent }),
      createLoggerLink(),
      ports.api({ headers }),
    ],
  });
  const api = createTRPCClientProxy<AppRouter>(trpcClient);
  const reporting = createPlayOutbox({
    storage: createPlayOutboxStorage(ports.db),
    send: (request) => api.analytics.playEvents.mutate(request),
    sendLegacy: (request) => api.analytics.trackStream.mutate(request),
    currentUid: () => ports.auth.currentUser()?.uid,
    deviceId: getPlayDeviceId,
    newPlayId: () => uuid.v4(),
    network: ports.network,
    appState: ports.appState,
    legacyStorage: ports.legacyPlayKv,
    exclusive: playOutboxLock,
    reportStorageError: (error) =>
      captureException(error, {
        channel: "error",
        area: "player",
        operation: "play-outbox-store",
      }),
  });
  const offlineAccess = createOfflineAccess({
    db: ports.db,
    auth: ports.auth,
    // What `useAuth` reads: the Firebase user it last observed, mirrored to
    // MMKV and restored into this query client when the session opens.
    savedUserId: () => {
      const saved = queries.queryClient.getQueryData<{ uid?: unknown } | null>([
        AUTH_QUERY_KEY,
      ]);
      return typeof saved?.uid === "string" ? saved.uid : null;
    },
    api,
    isCurrent,
    platform: ports.platform.os,
    now: ports.clock.now,
  });
  const session = {
    offlineAccess,
    generation,
    signal: controller.signal,
    isCurrent,
    staleWrites,
    api,
    reporting,
    trpcClient,
    utils: createTRPCQueryUtils<AppRouter>({
      client: trpcClient,
      queryClient: queries.queryClient,
    }),
    queryClient: queries.queryClient,
    db: ports.db,
    auth: ports.auth,
    cache,
    clearAuthMirror: queries.clearAuthMirror,
    dispose() {
      controller.abort();
      offlineAccess.dispose();
      reporting.dispose();
      queries.dispose();
    },
  };
  return {
    ...session,
    queries: {
      albums: (ids?: string[]) => albumsQuery(session, ids),
      playlist: (id: string) => playlistQuery(session, id),
    },
  };
}

export type DataSession = ReturnType<typeof openDataSession>;
